<template>
  <div class="page-admin-opportunities">
    <BaseLoadingBar is-small v-if="pending" />
    <div class="page-admin-opportunities__body">
      <!-- Phones: its main action, as the "+" of the settings header. -->
      <PageMainAction :label="$t('page.admin-opportunity-management.add-status')" @click="openAddStatus" />
      <div v-if="!isPhone" class="page-admin-opportunities__table-header-actions">
        <div class="page-admin__count">
          <span class="number-monospace">{{ opportunityStatuses.length }}</span>
          {{ ' ' + $tc('page.admin-opportunity-management.status', opportunityStatuses.length) }}
        </div>
        <div class="page-admin__actions">
          <BaseRefreshButton :size="40" :pending="pending" @refresh="onRefresh" />
          <BaseButton @click.native="openAddStatus" has-icon-left>
            <BaseIcon name="plus" />
            {{ $t('page.admin-opportunity-management.add-status') }}
          </BaseButton>
        </div>
      </div>
      <div class="page-admin-opportunities__table">
        <TableNormal
          v-sortable
          :headers="headers"
          :items="opportunityStatuses"
          :items-per-page="itemsToShow"
          :fixed-header="true"
          :single-select="false"
          hide-default-footer
          item-key="value"
          :mobile-breakpoint="620"
          @sorted="onTableSorted"
          @click:row="openEditStatus"
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ opportunityStatuses.length }}</span>
            {{ ' ' + $tc('page.admin-opportunity-management.status', opportunityStatuses.length) }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="onRefresh" />
          </template>
          <template v-slot:item.sort="{ item }">
            <!-- Disable dragging and dropping this row if it is reserved status -->
            <div :class="{ 'drag-handle': !item.isReserved, 'non-draggable': item.isReserved }">
              <BaseIcon name="menu" class="page-admin-opportunities__dnd-icon" />
            </div>
          </template>
          <template v-slot:item.name="{ item }">
            <!-- Phones: the tags' height, as the other settings lists' names (a label, a reason). -->
            <BaseStatusBadge
              :text="item.name"
              :color="item.color"
              :is-small="isPhone"
              class="page-admin-opportunities__badge"
            />
          </template>
          <template v-slot:item.statusType="{ item }">
            <OpportunityStatusType :opportunity-status="item" />
          </template>
          <template v-slot:item.actions="{ item }">
            <BaseMoreActions
              v-if="isPhone"
              :title="item.name"
              :actions="rowActions(item)"
              @select="onRowAction($event, item)"
            />
            <v-menu v-else>
              <template v-slot:activator="{ on, attrs }">
                <div @click.stop v-on="on" v-bind="attrs">
                  <BaseIcon name="more-horizontal" class="page-admin-opportunities__more" />
                </div>
              </template>
              <div class="page-admin-opportunities__actions-menu base-actions-menu">
                <ul>
                  <li class="edit" @click="openEditStatus(item)">
                    <BaseIcon :size="44" name="edit-2" />
                    {{ $t('actions.edit') }}
                  </li>
                  <li v-if="!item.isReserved" class="delete" @click="openDeleteStatus(item)">
                    <BaseIcon :size="44" name="trash-2" />
                    {{ $t('actions.delete') }}
                  </li>
                </ul>
              </div>
            </v-menu>
          </template>
        </TableNormal>
      </div>
    </div>
    <AddOpportunityStatusDialog ref="addOpportunityStatusDialog" @action-succeeded="onRefresh" />
    <EditOpportunityStatusDialog ref="editOpportunityStatusDialog" @action-succeeded="onRefresh" />
    <DeleteOpportunityStatusDialog ref="deleteOpportunityStatusDialog" @action-succeeded="onRefresh" />
  </div>
</template>

<script>
import BaseButton from '@/components/base/BaseButton';
import BaseIcon from '@/components/base/BaseIcon';
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import TableNormal from '@/components/table/TableNormal.vue';
import PageMainAction from '@/components/layout/PageMainAction.vue';
import { viewport } from '@/util/viewport';
import BaseMoreActions from '@/components/base/BaseMoreActions.vue';
import BaseStatusBadge from '@/components/base/BaseStatusBadge.vue';
import EditOpportunityStatusDialog from '@/components/page-admin-opportunities/EditOpportunityStatusDialog.vue';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import { mapActions, mapGetters } from 'vuex';
import AddOpportunityStatusDialog from '@/components/page-admin-opportunities/AddOpportunityStatusDialog.vue';
import OpportunityStatusType from '@/components/page-admin-opportunities/OpportunityStatusType.vue';
import DeleteOpportunityStatusDialog from '@/components/page-admin-opportunities/DeleteOpportunityStatusDialog.vue';

export default {
  name: 'PageAdminOpportunities',
  components: {
    BaseMoreActions,
    BaseRefreshButton,
    PageMainAction,
    DeleteOpportunityStatusDialog,
    OpportunityStatusType,
    BaseLoadingBar,
    AddOpportunityStatusDialog,
    EditOpportunityStatusDialog,
    BaseStatusBadge,
    TableNormal,
    BaseButton,
    BaseIcon,
  },
  data() {
    return {
      itemsToShow: 100,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('opportunityStatuses', ['opportunityStatuses', 'pending', 'error']),
    headers() {
      return [
        {
          text: '',
          sortable: false,
          value: 'sort',
          phone: 'hidden',
        },
        {
          text: this.$tc('page.admin-opportunity-management.status'),
          sortable: false,
          value: 'name',
          width: '20%',
        },
        {
          text: this.$t('page.admin-opportunity-management.description'),
          sortable: false,
          value: 'description',
          phone: 'subtitle',
        },
        {
          text: this.$t('page.admin-opportunity-management.status-type'),
          sortable: false,
          value: 'statusType',
          phone: 'meta',
          width: '15%',
        },
        {
          text: '',
          sortable: false,
          align: 'right',
          value: 'actions',
        },
      ];
    },
  },
  methods: {
    // The row's ⋯ on phones, a sheet (BaseMoreActions); the desktop's menu lists the same.
    rowActions(item) {
      return [
        { key: 'edit', label: this.$t('actions.edit'), icon: 'edit-2' },
        ...(item.isReserved
          ? []
          : [{ key: 'delete', label: this.$t('actions.delete'), icon: 'trash-2', danger: true }]),
      ];
    },
    onRowAction(key, item) {
      if (key === 'edit') this.openEditStatus(item);
      else this.openDeleteStatus(item);
    },
    ...mapActions('opportunityStatuses', ['getOpportunityStatuses', 'editOpportunityStatus']),
    openAddStatus() {
      this.$refs.addOpportunityStatusDialog.openDialog();
    },
    openEditStatus(status) {
      this.$refs.editOpportunityStatusDialog.openDialog(status);
    },
    openDeleteStatus(status) {
      this.$refs.deleteOpportunityStatusDialog.openDialog(status);
    },
    onRefresh() {
      this.getOpportunityStatuses();
    },
    onTableSorted(event) {
      const oldIndex = event.oldIndex;
      const newIndex = event.newIndex;
      const opportunityStatus = {
        ...this.opportunityStatuses[oldIndex],
        sortingOrder: newIndex + 1, // sortingOrder is 1-indexed
      };
      this.editOpportunityStatus({ opportunityStatusId: opportunityStatus.id, opportunityStatus })
        .then(() => {
          if (this.error) {
            this.$toast.error(this.error);
            return;
          }
          this.$toast.success(this.$t('page.admin-opportunity-management.edit-dialog.edit-opportunity-status-success'));
          this.$emit('action-succeeded');
        })
        .catch((error) => this.$toast.error(error))
        .finally(() => this.onRefresh());
    },
  },
  async created() {
    this.getOpportunityStatuses();
  },
};
</script>

<style lang="scss">
.page-admin-opportunities {
  &__dnd-icon {
    width: 18px;
    height: 18px;
    color: $color-normal-pale;
    margin-left: 16px;
  }

  &__body {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 32px;
  }

  &__table-header-actions {
    padding-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__badge {
    width: fit-content;
    padding: 8px 16px;
  }

  &__more {
    cursor: pointer;
    margin-right: 24px;
  }

  &__table {
    margin-bottom: 32px;
  }

  &__actions-menu {
    min-width: 140px;
    cursor: pointer;
  }

  .v-data-table {
    tr {
      &:first-child,
      &:last-child {
        td {
          &:first-child {
            .base-icon {
              color: $color-neutral;
            }
          }
        }
      }
    }
  }
}
</style>
